import { useTranslation } from "@sg/i18n";
import { invalidateCache, QueryKey } from "@sg/query";
import { notify } from "@sg/ui";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { AuditableSchema } from "~/types";
import { agent } from "~/util/agent";

const DocumentCreateSchema = z.object({
  id: z.string().uuid().min(1),
  file_name: z.string().min(1),
  extension: z.string().min(1),
  content_type: z.string().min(1),
  content_key: z.string().min(1),
});

const DocumentSchema = DocumentCreateSchema.merge(AuditableSchema);

type DocumentResourceType = z.infer<typeof DocumentSchema>;

const useDocsMetadata = (queryKey: (QueryKey | undefined)[], prefix?: string, enabled?: boolean) =>
  useQuery(queryKey, async () => agent(`${prefix}/docs/metadata`).json<DocumentResourceType[]>(), {
    enabled,
  });

const useDocMetadata = (queryKey: (QueryKey | undefined)[], prefix?: string, documentId?: string, enabled?: boolean) =>
  useQuery(queryKey, async () => agent(`${prefix}/docs/metadata/${documentId}`).json<DocumentResourceType>(), {
    enabled,
  });

const useUploadDoc = (prefix: string, queryKey?: QueryKey | undefined) => {
  const { t } = useTranslation();
  return useMutation<string, Error, FormData>(
    (data) => {
      return agent
        .post(`${prefix}/docs/`, {
          body: data,
        })
        .json();
    },
    {
      onSuccess: () => {
        if (queryKey) {
          invalidateCache(queryKey, null);
        }
      },
      onError: () => {
        notify.alert(t("upload_error"));
      },
    }
  );
};

const useDownloadDoc = (prefix: string) => {
  return useMutation<Blob, Error, string>((documentId) => {
    return agent(`${prefix}/docs/${documentId}`).blob();
  });
};

export type { DocumentResourceType };
export { DocumentCreateSchema, DocumentSchema, useDocsMetadata, useDocMetadata, useUploadDoc, useDownloadDoc };
